import { Box, Button, IconButton, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import scheduleEventApi from '~/apis/schedule';
import { ArrowLeftIcon, ArrowRightIcon, PlusIcon } from '~/assets/icons';
import Calendar from '~/components/schedules/calendar/Calendar';
import EditEventDialog from '~/components/schedules/edit-event-dialog/EditEventDialog';
import usePermission from '~/hooks/usePermission';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { jobActions } from '~/thunks/job/jobSlice';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { PERMISSIONS } from '~/utils/constants/permission';
import { handleApiError } from '~/utils/helper';
import { IEditEventPayload, IScheduleDayEventGroup, IScheduleEvent } from '~/utils/interface/schedule';
import { CalendarViewMode } from '~/utils/types/schedule';

type ScheduleProps = {
  jobId: string;
};

const Schedule = ({ jobId }: ScheduleProps) => {
  //#region Declare Hook
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['admin/schedule', 'admin/common']);
  const calendarViewMode = useRef<CalendarViewMode>('week');
  const permissions = usePermission();
  //#endregion Declare Hook

  //#region Selector
  const { scheduleCurrentTime, needRefreshScheduleData } = useAppSelector((state) => state.job);
  //#endregion Selector

  //#region Declare State
  const [dayEvents, setDayEvents] = useState<Array<IScheduleDayEventGroup>>([]);
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [editEventDialogMode, setEditEventDialogMode] = useState<'create' | 'edit'>(DIALOG_MODE_CREATE);
  const [editData, setEditData] = useState<IEditEventPayload | undefined>(undefined);
  //#endregion Declare State

  const timeValue = useMemo(() => {
    return dayjs(scheduleCurrentTime).startOf('week').add(1, 'day').format('dddd, MMMM DD');
  }, [scheduleCurrentTime]);

  const fetchWeekEvents = useCallback(async () => {
    try {
      const startDay = dayjs(scheduleCurrentTime).startOf(calendarViewMode.current).format('YYYY-MM-DD');
      const endDay = dayjs(scheduleCurrentTime).endOf(calendarViewMode.current).format('YYYY-MM-DD');

      const res = await scheduleEventApi.getDayEvents({
        mode: 'full',
        jobId: jobId,
        startDay: startDay,
        endDay: endDay,
      });

      setDayEvents(res.data.data.responses);
    } catch (error) {}
  }, [jobId, scheduleCurrentTime]);

  //#region Implement Hook
  useEffect(() => {
    fetchWeekEvents();
  }, [fetchWeekEvents]);

  useEffect(() => {
    if (needRefreshScheduleData) {
      fetchWeekEvents();
      dispatch(jobActions.setNeedRefreshScheduleData(false));
    }
  }, [dispatch, fetchWeekEvents, needRefreshScheduleData]);
  //#endregion Implement Hook

  const handleSelectEvent = async (event: IScheduleEvent) => {
    try {
      setEditEventDialogMode(DIALOG_MODE_EDIT);
      const selectedEvent = (await scheduleEventApi.getEvent(event.id)).data.data;
      setEditData(selectedEvent);
      setOpenEditDialog(true);
    } catch (error: unknown) {
      handleApiError(error);
    }
  };

  const handleSetOpen = useCallback(
    (open: boolean, needRefreshData?: boolean) => {
      setOpenEditDialog(open);
      if (needRefreshData) {
        dispatch(jobActions.setNeedRefreshScheduleData(needRefreshData));
      }
    },
    [dispatch]
  );

  return (
    <>
      {permissions[PERMISSIONS.schedule_create] && (
        <Box className='text-right'>
          <Button
            onClick={() => {
              setEditData(undefined);
              setEditEventDialogMode(DIALOG_MODE_CREATE);
              setOpenEditDialog(true);
            }}
            className='h-[36px] px-[14px] py-[9.5px] text-[14px]/[16.8px] font-medium rounded-lg'
            variant='contained'
            disableElevation
            startIcon={<PlusIcon className='w-[10px] h-[10px]' />}
          >
            {t('addSchedule')}
          </Button>
        </Box>
      )}
      <Box className='mt-[20px] mb-[20px] flex flex-col items-start lg:flex-row lg:items-center'>
        <Box className='flex flex-row flex-1'>
          <Box className='flex flex-row items-center'>
            <Box className='flex flex-row'>
              <IconButton
                color='primary'
                className='pl-[6px] pr-[4.5px] py-[7.5px] rounded-l-lg rounded-r-none bg-[#007AFC] hover:bg-[#007AFC]'
                onClick={() => {
                  dispatch(
                    jobActions.setScheduleCurrentTime(
                      dayjs(scheduleCurrentTime).subtract(1, calendarViewMode.current).format('YYYY-MM-DD')
                    )
                  );
                }}
              >
                <ArrowLeftIcon className='w-[13px] h-[13px]' />
              </IconButton>
              <IconButton
                color='primary'
                className='pl-[4.5px] pr-[6px] py-[7.5px] rounded-r-lg rounded-l-none bg-[#007AFC] hover:bg-[#007AFC]'
                onClick={() => {
                  dispatch(
                    jobActions.setScheduleCurrentTime(
                      dayjs(scheduleCurrentTime).add(1, calendarViewMode.current).format('YYYY-MM-DD')
                    )
                  );
                }}
              >
                <ArrowRightIcon className='w-[13px] h-[13px]' />
              </IconButton>
            </Box>
            <Button
              variant='outlined'
              className='text-[#797D8A] border border-[#E5EAEF] hover:border-[#E5EAEF] rounded-lg text-[14px]/[16.8px] font-medium ml-[8px] h-[28px] px-[9px] py-[5.5px]'
              onClick={() => {
                dispatch(jobActions.setScheduleCurrentTime(dayjs().format('YYYY-MM-DD')));
              }}
            >
              {t('today')}
            </Button>
          </Box>
          <Box className='flex flex-1 justify-center min-w-[220px]'>
            <Typography className='text-[20px]/[26.04px] text-[#42485B] font-medium' variant='h5'>
              {timeValue}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box className='h-[calc(100vh-260px)] overflow-auto mt-[20px]'>
        <Calendar
          view={calendarViewMode.current}
          date={scheduleCurrentTime}
          events={[]}
          dayEvents={dayEvents}
          onSelectEvent={handleSelectEvent}
        />
      </Box>

      <EditEventDialog
        open={openEditDialog}
        setOpen={handleSetOpen}
        mode={editEventDialogMode}
        data={editData}
        jobId={jobId}
      />
    </>
  );
};

export default Schedule;
